import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import {
  contractsApi,
  defaultColumnMapping,
  MAPPING_FIELD_LABELS,
  mappingTemplatesApi,
  projectsApi,
  structuresApi,
  uploadsApi,
} from '../../services/benchmarkApi';
import { projectLabel } from './benchmarkFormat';

const EMPTY_MAPPING = { ...defaultColumnMapping };

/**
 * Shared Benchmark Excel/CSV upload dialog.
 * Step 1: project/contract/classification + file (+ Contract vs Bid for estimate)
 * Step 2: map standard fields via dropdown of Excel headers; unmapped columns become JSON
 * Step 3: import summary
 */
const BenchmarkUploadDialog = ({
  open,
  onClose,
  uploadType = 'estimate',
  defaultProjectId,
  defaultContractId,
  /** When true (Estimate/Actual/CO screens), project+contract are fixed and not editable. */
  lockContext = false,
  onSuccess,
  /** Opens messy-Excel AI flow (standup feature F) instead of column mapping. */
  onOpenAiUpload,
}) => {
  const [projects, setProjects] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [structureTypes, setStructureTypes] = useState([]);
  const [projectId, setProjectId] = useState(defaultProjectId || '');
  const [contractId, setContractId] = useState(defaultContractId || '');
  const [primaryStructureType, setPrimaryStructureType] = useState('CBS');
  const [datasetKind, setDatasetKind] = useState('CONTRACT');
  const [bidderName, setBidderName] = useState('');
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState(EMPTY_MAPPING);
  const [templateName, setTemplateName] = useState('default');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateSaving, setTemplateSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);

  const isEstimateFamily = uploadType === 'estimate' || uploadType === 'bid';

  const title = useMemo(() => {
    if (uploadType === 'change-order') return 'Upload Change Order';
    if (uploadType === 'actual') return 'Upload Progress / Actual Cost';
    if (datasetKind === 'BID') return 'Upload Bid / Tender';
    return 'Upload Estimate / BoQ';
  }, [uploadType, datasetKind]);

  const mappedHeaders = useMemo(
    () => new Set(Object.values(mapping).filter(Boolean)),
    [mapping]
  );

  const unmappedHeaders = useMemo(
    () => headers.filter((h) => h && !mappedHeaders.has(h)),
    [headers, mappedHeaders]
  );

  const loadTemplates = async (pid) => {
    try {
      const res = await mappingTemplatesApi.list(uploadType, pid || undefined);
      setTemplates(res.data || []);
    } catch {
      setTemplates([]);
    }
  };

  useEffect(() => {
    if (!open) return;
    setError('');
    setSummary(null);
    setStep(1);
    setFile(null);
    setHeaders([]);
    setMapping({ ...EMPTY_MAPPING });
    setSelectedTemplateId('');
    setProjectId(defaultProjectId || '');
    setContractId(defaultContractId || '');
    setDatasetKind(uploadType === 'bid' ? 'BID' : 'CONTRACT');
    setBidderName('');
    projectsApi.list().then((res) => setProjects(res.data || [])).catch(() => setProjects([]));
    structuresApi.listTypes().then((res) => {
      const types = res.data || [];
      setStructureTypes(types);
      if (types.length && !types.find((t) => t.structureCode === 'CBS')) {
        setPrimaryStructureType(types[0].structureCode);
      }
    }).catch(() => setStructureTypes([]));
    loadTemplates(defaultProjectId || undefined);
  }, [open, defaultProjectId, defaultContractId, uploadType]);

  useEffect(() => {
    if (!projectId) {
      setContracts([]);
      return;
    }
    contractsApi.list(projectId).then((res) => setContracts(res.data || [])).catch(() => setContracts([]));
    if (step === 2) {
      loadTemplates(projectId);
    }
  }, [projectId]);

  const handleFile = async (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    const ext = selected.name.split('.').pop()?.toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setError('Please select .xlsx, .xls, or .csv');
      return;
    }
    setError('');
    setFile(selected);
    setPreviewLoading(true);
    try {
      const res = await uploadsApi.preview(selected);
      const data = res.data || {};
      const fileHeaders = data.headers || [];
      setHeaders(fileHeaders);
      const suggested = { ...EMPTY_MAPPING, ...(data.suggestedMapping || {}) };
      setMapping(suggested);
    } catch (e) {
      setHeaders([]);
      setMapping({ ...EMPTY_MAPPING });
      setError(e.message || 'Failed to read file headers');
    } finally {
      setPreviewLoading(false);
    }
  };

  const goToMapping = () => {
    if (!projectId) {
      setError('Select a project');
      return;
    }
    if (isEstimateFamily && datasetKind === 'BID' && !String(bidderName || '').trim()) {
      setError('Enter bidder / contractor name for Bid upload');
      return;
    }
    if (lockContext && !contractId) {
      setError('Contract is required for this upload');
      return;
    }
    if (!file) {
      setError('Select a file');
      return;
    }
    if (!headers.length) {
      setError('No columns detected in the file. Re-select the file.');
      return;
    }
    setError('');
    setStep(2);
    loadTemplates(projectId);
  };

  const handleUpload = async () => {
    if (!mapping.structureCode) {
      setError('Map Structure / Budget Code — this is required');
      return;
    }
    if (!mapping.description) {
      setError('Map Description — this is required');
      return;
    }
    if (isEstimateFamily && datasetKind === 'BID' && !String(bidderName || '').trim()) {
      setError('Enter bidder / contractor name for Bid upload');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        file,
        projectId,
        contractId: contractId || undefined,
        primaryStructureType,
        columnMapping: mapping,
        datasetKind: isEstimateFamily ? datasetKind : 'CONTRACT',
        bidderName: isEstimateFamily && datasetKind === 'BID' ? String(bidderName).trim() : undefined,
      };
      let res;
      if (uploadType === 'change-order') res = await uploadsApi.changeOrder(payload);
      else if (uploadType === 'actual') res = await uploadsApi.actual(payload);
      else if (datasetKind === 'BID') res = await uploadsApi.bid(payload);
      else res = await uploadsApi.estimate(payload);
      setSummary(res.data);
      setStep(3);
      if (onSuccess) onSuccess(res.data);
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    const name = (templateName || 'default').trim();
    if (!name) {
      setError('Enter a template name');
      return;
    }
    setTemplateSaving(true);
    setError('');
    try {
      const res = await mappingTemplatesApi.save({
        templateName: name,
        uploadType,
        projectId: projectId || null,
        mapping,
      });
      await loadTemplates(projectId || undefined);
      if (res.data?.templateId) {
        setSelectedTemplateId(String(res.data.templateId));
      }
    } catch (e) {
      setError(e.message || 'Failed to save template');
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleLoadTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    const tpl = templates.find((t) => String(t.templateId) === String(templateId));
    if (!tpl) return;
    setTemplateName(tpl.templateName || 'default');
    const next = { ...EMPTY_MAPPING };
    Object.entries(tpl.mapping || {}).forEach(([field, header]) => {
      if (header && headers.includes(header)) {
        next[field] = header;
      }
    });
    setMapping(next);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudUpload /> {title}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {step === 1 && (
          <Grid container spacing={2}>
            {lockContext && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Upload is scoped to the project and contract you opened. To choose a different
                  project/contract, use Dashboard → Upload.
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" disabled={lockContext}>
                <InputLabel>Project</InputLabel>
                <Select
                  label="Project"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                >
                  {projects.map((p) => (
                    <MenuItem key={p.id || p.projectId} value={p.id || p.projectId}>
                      {projectLabel(p)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" disabled={lockContext}>
                <InputLabel>{lockContext ? 'Contract' : 'Contract (optional)'}</InputLabel>
                <Select
                  label={lockContext ? 'Contract' : 'Contract (optional)'}
                  value={contractId}
                  onChange={(e) => setContractId(e.target.value)}
                >
                  {!lockContext && <MenuItem value="">None</MenuItem>}
                  {contracts.map((c) => (
                    <MenuItem key={c.id || c.contractId} value={c.id || c.contractId}>
                      {c.contractNumber || c.contractName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {isEstimateFamily && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Upload type
                </Typography>
                <RadioGroup
                  row
                  value={datasetKind}
                  onChange={(e) => {
                    setDatasetKind(e.target.value);
                    if (e.target.value !== 'BID') setBidderName('');
                  }}
                >
                  <FormControlLabel value="CONTRACT" control={<Radio size="small" />} label="Contract upload" />
                  <FormControlLabel value="BID" control={<Radio size="small" />} label="Bid upload" />
                </RadioGroup>
                {datasetKind === 'BID' && (
                  <TextField
                    size="small"
                    fullWidth
                    required
                    label="Bidder / Contractor name"
                    value={bidderName}
                    onChange={(e) => setBidderName(e.target.value)}
                    placeholder="e.g. ACC, MARCO, TPMW"
                    sx={{ mt: 1, maxWidth: 420 }}
                  />
                )}
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Primary Classification</InputLabel>
                <Select
                  label="Primary Classification"
                  value={primaryStructureType}
                  onChange={(e) => setPrimaryStructureType(e.target.value)}
                >
                  {structureTypes.map((t) => (
                    <MenuItem key={t.structureTypeId} value={t.structureCode}>
                      {t.structureCode} — {t.structureName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" startIcon={<CloudUpload />} disabled={previewLoading}>
                {previewLoading ? 'Reading file…' : 'Choose Excel / CSV'}
                <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} />
              </Button>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {file.name}
                  {headers.length ? ` (${headers.length} columns detected)` : ''}
                </Typography>
              )}
              {isEstimateFamily && typeof onOpenAiUpload === 'function' && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Messy multi-bidder Excel that won&apos;t map cleanly?
                  </Typography>
                  <Button size="small" variant="text" onClick={onOpenAiUpload} sx={{ textTransform: 'none', px: 0 }}>
                    Upload with AI instead →
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        )}

        {step === 2 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Map standard fields — select Excel column from dropdown
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Structure / Budget Code is required so Estimate / BoQ and Actual can match on the same code.
              Unmapped Excel columns are saved as JSON and can be viewed in a side drawer after import.
            </Typography>

            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={8}>
                <TextField
                  size="small"
                  fullWidth
                  label="Template name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                />
              </Grid>
              <Grid item xs={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleSaveTemplate}
                  disabled={templateSaving}
                >
                  {templateSaving ? 'Saving…' : 'Save template'}
                </Button>
              </Grid>
              {templates.length > 0 && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Load template</InputLabel>
                    <Select
                      label="Load template"
                      value={selectedTemplateId}
                      onChange={(e) => handleLoadTemplate(e.target.value)}
                    >
                      {templates.map((t) => (
                        <MenuItem key={t.templateId} value={String(t.templateId)}>
                          {t.templateName}
                          {t.projectId ? ' (this project)' : ' (tenant)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>

            <Grid container spacing={1.5}>
              {Object.keys(MAPPING_FIELD_LABELS).map((key) => (
                <Grid item xs={12} md={6} key={key}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{MAPPING_FIELD_LABELS[key]}</InputLabel>
                    <Select
                      label={MAPPING_FIELD_LABELS[key]}
                      value={mapping[key] || ''}
                      onChange={(e) =>
                        setMapping((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                    >
                      <MenuItem value="">
                        <em>Not mapped</em>
                      </MenuItem>
                      {headers.map((h) => (
                        <MenuItem key={h} value={h}>
                          {h}
                          {mappedHeaders.has(h) && mapping[key] !== h ? ' (used)' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Unmapped columns → saved as JSON ({unmappedHeaders.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {unmappedHeaders.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    All Excel columns are mapped to standard fields.
                  </Typography>
                ) : (
                  unmappedHeaders.map((h) => (
                    <Chip key={h} size="small" label={h} variant="outlined" />
                  ))
                )}
              </Box>
            </Box>
          </Box>
        )}

        {step === 3 && summary && (
          <Box>
            <Alert severity={summary.uploadStatus === 'FAILED' ? 'error' : 'success'} sx={{ mb: 2 }}>
              Status: {summary.uploadStatus} — {summary.successfulRows}/{summary.totalRows} rows imported
              {summary.rejectedRows ? `, ${summary.rejectedRows} rejected` : ''}
            </Alert>
            {summary.errors?.length > 0 && (
              <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
                {summary.errors.map((err, idx) => (
                  <Typography key={idx} variant="body2" color="error">
                    Row {err.rowNumber}: [{err.columnName}] {err.errorMessage}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {step === 1 && (
          <Button
            variant="contained"
            disabled={!file || !projectId || previewLoading || !headers.length}
            onClick={goToMapping}
          >
            Next: Mapping
          </Button>
        )}
        {step === 2 && (
          <>
            <Button onClick={() => setStep(1)}>Back</Button>
            <Button variant="contained" disabled={loading} onClick={handleUpload}>
              {loading ? 'Uploading…' : 'Upload'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BenchmarkUploadDialog;
